import { Focus } from './typings';
import 'focus-options-polyfill';
import clamp from '../utils/clamp';
import { Keys } from '../utils/keys';
import { ScrollDirection } from '../appTypings';

/*
 * use functions instead of static arrays since otherwise values get
 * inlined at build time and break, as keycodes differ based on client
 */
export const isLRUDKey = (keyCode: number) =>
  keyCode === Keys.VK_LEFT ||
  keyCode === Keys.VK_RIGHT ||
  keyCode === Keys.VK_UP ||
  keyCode === Keys.VK_DOWN;

/*
 * use functions instead of static arrays since otherwise values get
 * inlined at build time and break, as keycodes differ based on client
 */
export const isBackKey = (keyCode: number) =>
  keyCode === Keys.VK_BACK || keyCode === Keys.VK_BACKSPACE;

export const plaintext = {
  [Keys.VK_LEFT]: 'left',
  [Keys.VK_RIGHT]: 'right',
  [Keys.VK_UP]: 'up',
  [Keys.VK_DOWN]: 'down',
  [Keys.VK_BACKSPACE]: 'back',
  [Keys.VK_BACK]: 'back',
};

export const directionToKeyCode: Record<ScrollDirection, number> = {
  left: Keys.VK_LEFT,
  right: Keys.VK_RIGHT,
  up: Keys.VK_UP,
  down: Keys.VK_DOWN,
};

/**
 * Development helper
 */
export const activeElementTracker = () => {
  const active = document.activeElement;
  console.log(`focused ${active?.id}`, document.activeElement);

  const blurListener = () => {
    console.log(`blurred ${active?.id}`);
    active?.removeEventListener('blur', blurListener);
  };
  document.activeElement?.addEventListener('blur', blurListener);
};

export const blur = (focus: Focus) => {
  document.getElementById(focus.id)?.blur();
};

/**
 * Focuses navigation focus, and returns whether the click was performed
 * @param focus
 * @param shouldClick
 * @returns was focus clicked
 */
export const click = (focus: Focus, shouldClick = true): boolean => {
  const focusedElement = document.getElementById(focus.id);

  if (
    focusedElement &&
    focusedElement.id === document.activeElement?.id &&
    shouldClick
  ) {
    focusedElement.click();

    return true;
  }

  return false;
};

/**
 * Uses HTMLElement.focus() on element which is pointed by [focus]{@link external:Focus}-parameter if the element is not already focused.
 * @param {Focus} focus object which contains id of the element to be focused
 * @param shouldFocus should the element be focused: when false, the function does nothing
 * @param isDynamicContainer when true, element with id '[focus.row]{@link external:Focus}/[focus.offset]{@link external:Focus}' is used. Otherwise [focus.id]{@link external:Focus}
 * @param onFocus additional side-effect which is performed after focusing the element
 */
export const triggerFocus = (
  focus: Focus,
  shouldFocus: boolean,
  onFocus?: (e: Element | null) => void
) => {
  if (shouldFocus && window.document.activeElement?.id !== focus.id) {
    const element = document.getElementById(focus.id);

    // scrolling on focus blocked to prevent any accidental scrolling, when
    // focused element is fully or slightly outside the visible content.
    element?.focus({ preventScroll: true });
    onFocus?.(element);
  }
};

export const fromCoords = ({
  row,
  offset,
  column,
}: Omit<Focus, 'id'>): Focus => ({
  id: idFromCoords({ row, offset, column }),
  row,
  offset,
  column,
});

export function getFocusWithValidOffset(
  { offset, ...focus }: Focus,
  container: unknown[]
) {
  const validOffset = clamp(offset, 0, container.length - 1);

  return fromCoords({ ...focus, offset: validOffset });
}

export const idFromCoords = ({ row, offset, column }: Omit<Focus, 'id'>) =>
  `${row}/${column}/${offset}`;
